import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Button from '../../components/common/Button/Button';
import Card from '../../components/common/Card/Card';
import ImagePlaceholder from '../../components/common/ImagePlaceholder/ImagePlaceholder';
import HeroSlider from '../../components/common/HeroSlider/HeroSlider';
import NewsPopup from '../../components/common/NewsPopup/NewsPopup';
import { APP_CONFIG } from '../../constants/config';
import { ROUTES } from '../../constants/routes';
import { getLatestNews, formatNewsDate } from '../../constants/newsData';
import { HERO_SLIDES } from '../../constants/heroSlides';
import './Home.css';

/**
 * Página de Inicio - Colegio Bertolt Brecht
 */
const Home = () => {
    const [showNewsPopup, setShowNewsPopup] = useState(true);
    const latestNews = getLatestNews(1)[0];

    return (
        <div className="home">
            {/* News Popup */}
            {showNewsPopup && latestNews && (
                <NewsPopup
                    news={latestNews}
                    onClose={() => setShowNewsPopup(false)}
                />
            )}

            {/* Hero Slider */}
            <HeroSlider slides={HERO_SLIDES} autoPlayInterval={6000} />

            {/* Secciones Destacadas */}
            <section className="home__highlights">
                <div className="container">
                    <h2 className="text-center animate-slide-up">Descubre Nuestra Institución</h2>
                    <div className="home__highlights-grid">
                        <Link to={ROUTES.PROGRAMS} className="home__highlight-link">
                            <Card hoverable className="home__highlight-card animate-scale-in animate-delay-1">
                                <ImagePlaceholder
                                    aspectRatio="4/3"
                                    icon="📚"
                                    alt="Propuesta Educativa"
                                />
                                <div className="home__highlight-content">
                                    <h3>Propuesta Educativa</h3>
                                    <p>Metodología propia enfocada en el aprendizaje integral del estudiante</p>
                                    <span className="home__highlight-arrow">Ver más →</span>
                                </div>
                            </Card>
                        </Link>

                        <Link to={ROUTES.WORKSHOPS} className="home__highlight-link">
                            <Card hoverable className="home__highlight-card animate-scale-in animate-delay-2">
                                <ImagePlaceholder
                                    aspectRatio="4/3"
                                    icon="🎨"
                                    alt="Talleres"
                                />
                                <div className="home__highlight-content">
                                    <h3>Talleres</h3>
                                    <p>Música, deporte, inglés, oratoria y más actividades extracurriculares</p>
                                    <span className="home__highlight-arrow">Ver más →</span>
                                </div>
                            </Card>
                        </Link>

                        <Link to={ROUTES.CONTACT} className="home__highlight-link">
                            <Card hoverable className="home__highlight-card animate-scale-in animate-delay-3">
                                <ImagePlaceholder
                                    aspectRatio="4/3"
                                    icon="📞"
                                    alt="Contacto"
                                />
                                <div className="home__highlight-content">
                                    <h3>Contacto</h3>
                                    <p>Estamos en {APP_CONFIG.contact.fullAddress}</p>
                                    <span className="home__highlight-arrow">Contáctanos →</span>
                                </div>
                            </Card>
                        </Link>
                    </div>
                </div>
            </section>

            {/* Nuestra Esencia */}
            <section className="home__essence">
                <div className="container">
                    <div className="home__essence-content">
                        <div className="home__essence-text animate-slide-in-left">
                            <h2>Nuestra Esencia</h2>
                            <div className="home__essence-items">
                                <div className="home__essence-item">
                                    <span className="home__essence-icon">⭐</span>
                                    <p>Más de <strong>{APP_CONFIG.yearsOfExperience} años</strong> de experiencia educativa</p>
                                </div>
                                <div className="home__essence-item">
                                    <span className="home__essence-icon">🎯</span>
                                    <p>Metodología propia: <strong>"Enfocado en el Aprendizaje"</strong></p>
                                </div>
                                <div className="home__essence-item">
                                    <span className="home__essence-icon">💡</span>
                                    <p>Comprometidos con el <strong>desarrollo integral</strong> del estudiante</p>
                                </div>
                            </div>
                        </div>
                        <div className="home__essence-image animate-slide-in-right">
                            <ImagePlaceholder
                                aspectRatio="1/1"
                                icon="🏫"
                                alt="Colegio Bertolt Brecht"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* Valores */}
            <section className="home__values">
                <div className="container">
                    <h2 className="text-center animate-slide-up">Nuestros Valores</h2>
                    <div className="home__values-grid">
                        {APP_CONFIG.values.map((value, index) => (
                            <div
                                key={value.name}
                                className={`home__value-item animate-scale-in animate-delay-${Math.min(index + 1, 4)}`}
                            >
                                <div className="home__value-icon">{getValueIcon(value.name)}</div>
                                <h4>{value.name}</h4>
                                <p>{value.description}</p>
                            </div>
                        ))}
                    </div>
                    <div className="home__ethical-principle animate-fade-in">
                        <p className="home__ethical-text">
                            <strong>Principio Ético:</strong> "{APP_CONFIG.ethicalPrinciple}"
                        </p>
                    </div>
                </div>
            </section>

            {/* Últimas Noticias */}
            <section className="home__news">
                <div className="container">
                    <div className="home__news-header">
                        <h2 className="text-center animate-slide-up">Últimas Noticias</h2>
                        <p className="home__news-subtitle">Mantente informado sobre nuestras actividades y logros</p>
                    </div>
                    <div className="home__news-grid">
                        {getLatestNews(3).map((newsItem, index) => (
                            <Link
                                key={newsItem.id}
                                to={ROUTES.NEWS}
                                className="home__news-link"
                            >
                                <Card hoverable className={`home__news-card animate-scale-in animate-delay-${index + 1}`}>
                                    <ImagePlaceholder
                                        src={newsItem.image}
                                        aspectRatio="16/9"
                                        icon="📰"
                                        alt={newsItem.imageAlt || newsItem.title}
                                    />
                                    <div className="home__news-content">
                                        <span className="home__news-category">{newsItem.category}</span>
                                        <h3 className="home__news-title">{newsItem.title}</h3>
                                        <p className="home__news-date">📅 {formatNewsDate(newsItem.date)}</p>
                                        <p className="home__news-excerpt">{newsItem.excerpt}</p>
                                        <span className="home__news-arrow">Leer más →</span>
                                    </div>
                                </Card>
                            </Link>
                        ))}
                    </div>
                    <div className="home__news-cta">
                        <Link to={ROUTES.NEWS}>
                            <Button variant="outline" size="md">
                                Ver todas las noticias
                            </Button>
                        </Link>
                    </div>
                </div>
            </section>

            {/* Call to Action */}
            <section className="home__cta">
                <div className="container">
                    <div className="home__cta-content animate-scale-in">
                        <h2>¿Listo para formar parte de nuestra comunidad?</h2>
                        <p>Únete a más de {APP_CONFIG.yearsOfExperience} años de excelencia educativa</p>
                        <Link to={ROUTES.ADMISSIONS}>
                            <Button variant="secondary" size="lg">
                                Solicita Información - Admisión 2026
                            </Button>
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
};

// Helper function para iconos de valores
const getValueIcon = (valueName) => {
    const icons = {
        'Liderazgo': '👑',
        'Participación': '🤝',
        'Solidaridad': '❤️',
        'Responsabilidad': '✅',
        'Disciplina': '📋'
    };
    return icons[valueName] || '⭐';
};

export default Home;
